import { css, html, LitElement, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { api } from './api.js';

import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlSwitch } from '@shoelace-style/shoelace';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { BillingEmailDetail } from './components/pick-billing-email.js';
import { OrgSlugPicked } from './components/pick-org-name.js';
import { consume, contexts, FKOrgChanged, TaskEvent } from './contexts.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { PageOrganization } from './page-organization.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { tasks } from './tasks.js';

const pageSchema = {
  planId: URLState.option<string|undefined>({
    name: 'plan_id',
    default: undefined,
    encode: filter => filter,
    decode: filter => filter,
  }),
  planName: URLState.option<string|undefined>({
    name: 'plan_name',
    default: undefined,
    encode: filter => filter?.toLowerCase().trim(),
    decode: filter => filter.toLowerCase().trim(),
  }),
  planBilling: URLState.option<string|undefined>({
    name: 'billing',
    default: undefined,
    encode: filter => filter?.toLowerCase().trim(),
    decode: filter => filter.toLowerCase().trim(),
  }),
};

@customElement('page-new-organization')
export class PageNewOrganization extends LitElement {

  static url(): string {
    return platformUrl('/new-organization');
  }

  static routes(): RouteConfig[] {
    return [{
      path: [ '/new-organization', ],
      render: () => html`<page-new-organization></page-new-organization>`,
    }];
  }

  private _plansTask = new Task(this, {
    args: () => [] as const,
    task: async ([], { signal }) => {
      return await api.productPlans.listPlans.GET(undefined, { signal });
    }
  });

  private _preselectPlanTask = new Task(this, {
    args: () => [this._plansTask.value, this._urlState.state.planName, this._urlState.state.planBilling] as const,
    task: async ([allPlans, planName, planBilling], { signal }) => {
      CHECK(allPlans && planName && planBilling);
      const selectedPlan = allPlans.find(plan => {
        if (plan.name.toLowerCase().trim() !== this._urlState.state.planName)
          return false;
        if (plan.billing === 'year' && this._urlState.state.planBilling === 'annual')
          return true;
        if (plan.billing === 'month' && this._urlState.state.planBilling === 'monthly')
          return true;
        return false;
      });
      if (!selectedPlan)
        return;
      this._urlState.update({
        planId: selectedPlan.id,
        planName: undefined,
        planBilling: undefined,
      });
    }
  });

  @consume({ context: contexts.user, subscribe: true }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @state() private _orgSlugResult?: TaskEvent<OrgSlugPicked>;
  @state() private _isCreatingOrganzation: boolean = false;
  @state() private _billingEmail?: BillingEmailDetail;

  @query('#annual') private _htmlAnnual?: SlSwitch;

  private _urlState = new URLState(this, pageSchema);

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'New Organization' }),
  });

  render() {
    if (!this._user)
      return;
    const allPlans = (this._plansTask.value ?? []);
    const selectedPlan = allPlans.find(plan => plan.id === this._urlState.state.planId);
    const showAnnual = (selectedPlan && selectedPlan.billing === 'year') || !!this._htmlAnnual?.checked;
    const plans = allPlans.filter(plan => plan.isImplicit || plan.billing === (showAnnual ? 'year' : 'month'));
    const planIsNotSelected = (this._serverInfo?.enabledBilling && !this._urlState.state.planId);
    return html`
      <app-header .customTitle=${"New Organization"}></app-header>
      <app-body>
        <fk-callout variant=tip>
          <i>Organizations</i> are groups of users with shared permissions that contain your projects. Think of them as your company, team, or personal workspace where you manage access control and billing.
        </fk-callout>

        <form @submit=${this._submitForm}>
          <v-box>
          <h1>Create a new organization</h1>

          <pick-org-name
            @org-slug=${(event: TaskEvent<OrgSlugPicked>) => this._orgSlugResult = event }
          ></pick-org-name>

          <pick-billing-email
            .initial=${this._user?.email}
            @billing-email=${(e: CustomEvent<BillingEmailDetail>) => this._billingEmail = e.detail}
          ></pick-billing-email>

          ${this._serverInfo?.enabledBilling ? html`
            <h1>Choose Your Plan</h1>
            <h-box>
              <x-filler></x-filler>
              <sl-switch id=annual ?checked=${selectedPlan ? selectedPlan.billing === 'year' : this._htmlAnnual?.checked} @sl-input=${() => {
                this._urlState.update({ planId: undefined });
                this.requestUpdate();
              }}>Annual Billing <sl-tag pill size=small variant=success>1 month free</sl-tag></sl-switch>
            </h-box>
            <product-plans-view
              plan-selector
              .selectedPlanId=${this._urlState.state.planId}
              .plans=${plans}
              .trialUsedAtSeconds=${this._user?.trialUsedAtSeconds}
              @fk-select=${async (event: CustomEvent<WireTypes.ProductPlan>) => {
                this._urlState.update({ planId: event.detail.id });
              }}
            ></product-plans-view>
          ` :
          nothing}
          <div class=actions>
            <sl-button ?disabled=${!this._formData() || this._isCreatingOrganzation || planIsNotSelected } variant=success type=submit>
              ${!this._orgSlugResult?.data?.orgSlug ? html`
                Choose unique organization name
              ` : !this._formData() ? html`
                Enter a billing email
              ` : planIsNotSelected ? html`
                Select your plan
              ` : this._isCreatingOrganzation && this._serverInfo?.enabledBilling && selectedPlan?.trialDays && !this._user?.trialUsedAtSeconds ? html`
                Starting free trial...
              ` : this._isCreatingOrganzation ? html`
                Creating organization...
              ` : html`
                Create organization
              `}
            </sl-button>
          </div>
          </v-box>
        </form>
      </app-body>
      <app-footer></app-footer>
    `
  }

  private _formData() {
    const orgSlug = this._orgSlugResult?.data?.orgSlug;
    if (!orgSlug)
      return undefined;
    if (!this._billingEmail?.valid)
      return undefined;
    return { orgSlug, billingEmail: this._billingEmail.value };
  }

  private async _submitForm(event: SubmitEvent) {
    event.preventDefault();
    try {
      this._isCreatingOrganzation = true;

      const formData = this._formData();
      if (!formData)
        return;

      await api.user.createOrganization.POST({
        orgSlug: formData.orgSlug,
        billingEmail: formData.billingEmail,
      });

      if (this._urlState.state.planId) {
        const url = await api.billing.setPlan.POST({ orgSlug: formData.orgSlug, planId: this._urlState.state.planId });
        if (url) {
          // Inplace navigation to billing.
          window.location.href = url;
        } else {
          this._router?.navigate(PageOrganization.url({ orgSlug: formData.orgSlug }));
          this.dispatchEvent(new FKOrgChanged({ orgSlug: formData.orgSlug }));
        }
      } else {
        this._router?.navigate(PageOrganization.url({ orgSlug: formData.orgSlug }));
      }
    } finally {
      this._isCreatingOrganzation = false;
    }
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles, css`
    footer {
      display: grid;
      grid-template-columns: 1fr auto;

      & sl-button {
        grid-column: 2;
      }
    }

    .project-details {
      color: var(--sl-color-neutral-400);
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--sl-spacing-small);
      margin-top: var(--sl-spacing-2x-large);
    }

    sl-tab-panel::part(base) {
      padding: 0;
    }
  `];
}
